import React, { useState, useMemo, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { User, Users, Save } from 'lucide-react';

import { AppState, AppAction, UserProfile, TemplateType, DataScope, WidgetId, EngineEvent } from '@/types/index';
import { useDashboardLayout } from '../features/dashboard-engine/hooks/useDashboardLayout';
import { useRoleView } from '../hooks/useRoleView';

import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { GettingStartedWidget } from '../components/onboarding/GettingStartedWidget';
import { useOnboardingAutoDetect } from '../hooks/useOnboardingAutoDetect';
import { DashboardToolbar } from '../features/dashboard-engine/components/DashboardToolbar';
import { DashboardGrid } from '../features/dashboard-engine/components/DashboardGrid';
import { WidgetSidebar } from '../features/dashboard-engine/components/WidgetSidebar';

import { CalendarAgenda } from '../features/dashboard-engine/widgets/CalendarAgenda';
import { TaskPulse } from '../features/dashboard-engine/widgets/TaskPulse';
import { ActivityFeed } from '../features/dashboard-engine/widgets/ActivityFeed';
import { MaintenanceStatus } from '../features/dashboard-engine/widgets/MaintenanceStatus';
import { SparkEngine } from '../features/dashboard-engine/widgets/SparkEngine';
import { QuickActions } from '../features/dashboard-engine/widgets/QuickActions';
import { BillsWidget } from '../features/dashboard-engine/widgets/BillsWidget';
import { PetsWidget } from '../features/dashboard-engine/widgets/PetsWidget';
import { VehiclesWidget } from '../features/dashboard-engine/widgets/VehiclesWidget';
import LeaderboardWidget from '../features/dashboard-engine/widgets/LeaderboardWidget';
import { DailyBriefWidget } from '../features/dashboard-engine/widgets/DailyBriefWidget';
import { HealthScoreWidget } from '../features/dashboard-engine/widgets/HealthScoreWidget';
import FinanceWidget from '../features/dashboard-engine/widgets/FinanceWidget';
import { MealPlannerWidget } from '../features/dashboard-engine/widgets/MealPlannerWidget';
import CoParentingWidget from '../features/dashboard-engine/widgets/CoParentingWidget';
import ElderCareWidget from '../features/dashboard-engine/widgets/ElderCareWidget';
import { ApplianceWidget } from '../features/dashboard-engine/widgets/ApplianceWidget';
import LocationWidget from '../features/dashboard-engine/widgets/LocationWidget';

interface DashboardProps {
  state: AppState;
  dispatch: Dispatch<AppAction>;
}

export const Dashboard: React.FC<DashboardProps> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const roleView = useRoleView(state.currentUser ?? null, state.households.find(h => h.id === state.currentHouseholdId) ?? null);

  const currentHousehold = state.households.find(h => h.id === state.currentHouseholdId) ?? null;
  const conditionalFlags = useMemo(() => ({
    hasCoParenting: !!currentHousehold?.coParentingLinkId,
    hasElderCare: ((state as any).careProfiles?.length ?? 0) > 0,
    hasMunicipal: ((currentHousehold as any)?.subscribedMunicipalityIds?.length ?? 0) > 0,
  }), [currentHousehold, state]);

  useOnboardingAutoDetect({
    householdId: state.currentHouseholdId,
    userId: state.currentUser?.id,
  });

  const activeScope = state.activeDataScope || 'PERSONAL';

  const {
    layouts,
    isEditMode,
    hasUnsavedChanges,
    toggleEditMode,
    onLayoutChange,
    addWidget,
    removeWidget,
    saveLayout,
    resetLayout
  } = useDashboardLayout(state.currentUser?.id, state.currentUser?.dashboardLayout);

  const scopedTasks = useMemo(() => {
    return state.tasks.filter(t => {
      const isPersonal = t.scope === 'personal' || !t.scope;
      if (activeScope === 'PERSONAL') return isPersonal;
      if (activeScope === 'SHARED') return !isPersonal;
      return true;
    });
  }, [state.tasks, activeScope]);

  const scopedEvents = useMemo(() => {
    let allEvents: EngineEvent[] = [];
    if (activeScope === 'PERSONAL') {
      allEvents = state.personalEvents ?? [];
    } else if (activeScope === 'SHARED') {
      allEvents = state.householdEvents ?? [];
    } else {
      allEvents = [...(state.personalEvents ?? []), ...(state.householdEvents ?? [])];
    }
    return allEvents;
  }, [state.personalEvents, state.householdEvents, activeScope]);

  const ADMIN_ONLY_WIDGETS: WidgetId[] = ['activity_log', 'leaderboard', 'bills_summary', 'sparks'];
  const MEMBER_HIDDEN_WIDGETS: WidgetId[] = ['activity_log', 'leaderboard', 'bills_summary'];

  const widgetRegistry = useMemo((): Partial<Record<WidgetId, React.ReactNode>> => {
    return {
      quick_access: (
        <QuickActions
          dispatch={dispatch}
          currentUserId={state.currentUser!.id}
          currentHouseholdId={state.currentHouseholdId}
          activeScopeToggle={activeScope === 'PERSONAL' ? 'personal' : activeScope === 'SHARED' ? 'shared' : 'both'}
          onAction={(type, _scope) => {
            if (type === 'task') navigate('/tasks');
            if (type === 'event') navigate('/calendar');
            if (type === 'grocery') navigate('/groceries');
          }}
        />
      ),
      calendar_agenda: (
        <CalendarAgenda
          events={scopedEvents}
          onEventClick={(_ev) => navigate('/calendar')}
          activeScope={activeScope}
          currentHouseholdId={state.currentHouseholdId}
        />
      ),
      tasks_summary: (
        <TaskPulse
          tasks={scopedTasks.filter(t => t.templateType === TemplateType.ACTION)}
          currentUserId={state.currentUser!.id}
          currentHouseholdId={state.currentHouseholdId}
        />
      ),
      maintenance_agenda: (
        <MaintenanceStatus
          tasks={scopedTasks.filter(t => t.templateType === TemplateType.MAINTENANCE)}
          activeScope={activeScope}
          currentUserId={state.currentUser!.id}
          currentHouseholdId={state.currentHouseholdId}
        />
      ),
      activity_log: (
        <ActivityFeed
          logs={state.auditLogs?.filter(l => {
             if (activeScope === 'PERSONAL') return l.scope === 'personal';
             if (activeScope === 'SHARED') return l.scope === 'household';
             return true;
          }) || []}
          currentHouseholdId={state.currentHouseholdId}
          activeScope={activeScope}
        />
      ),
      sparks: (
        <SparkEngine
          insights={state.intelligence?.sparks || []}
          currentHouseholdId={state.currentHouseholdId}
          activeScope={activeScope}
        />
      ),
      bills_summary: <BillsWidget householdId={state.currentHouseholdId} />,
      pets_overview: <PetsWidget householdId={state.currentHouseholdId} />,
      vehicles_summary: <VehiclesWidget householdId={state.currentHouseholdId} />,
      leaderboard: <LeaderboardWidget householdId={state.currentHouseholdId} currentUserId={state.currentUser!.id} />,
      daily_brief: <DailyBriefWidget userId={state.currentUser?.id ?? null} />,
      health_score: <HealthScoreWidget state={state} />,
      finance_summary: <FinanceWidget householdId={state.currentHouseholdId} />,
      meal_planner_week: <MealPlannerWidget householdId={state.currentHouseholdId} userId={state.currentUser!.id} />,
      co_parenting_summary: currentHousehold?.coParentingLinkId ? (
        <CoParentingWidget linkId={currentHousehold.coParentingLinkId} />
      ) : null,
      appliance_overview: <ApplianceWidget householdId={state.currentHouseholdId} />,
      elder_care_summary: conditionalFlags.hasElderCare ? (
        <ElderCareWidget careProfileIds={((state as any).careProfiles ?? []).map((p: any) => p.id)} />
      ) : null,
      location_overview: <LocationWidget householdId={state.currentHouseholdId} />,
    };
  }, [state, dispatch, scopedTasks, scopedEvents, activeScope, navigate, currentHousehold, conditionalFlags]);

  if (!state.currentUser) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 dark:bg-slate-950" role="status">
        <div className="text-center space-y-4">
          <LoadingSpinner size="lg" className="mx-auto" />
          <p className="text-2xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {t('auth.syncing', 'Loading Your Dashboard...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-500">

      <DashboardToolbar
        isEditMode={isEditMode}
        onToggleEdit={toggleEditMode}
        onResetLayout={resetLayout}
        onSaveLayout={() => {
          saveLayout();
          dispatch({ type: 'UPDATE_LAYOUT', payload: layouts });
        }}
        onAddWidgetClick={() => setIsSidebarOpen(true)}
        hasUnsavedChanges={hasUnsavedChanges}
        lastSynced={state.lastSyncTimestamp ? new Date(state.lastSyncTimestamp) : undefined}
        roleView={roleView}
        activeScope={activeScope}
        onScopeChange={(scope: DataScope) => dispatch({ type: 'SET_DATA_SCOPE', payload: scope })}
        currentHouseholdId={state.currentHouseholdId}
      />

      <div className="flex flex-1 relative overflow-hidden">

        <main className="flex-1 overflow-y-auto no-scrollbar pb-24">
          <div className="max-w-[1600px] mx-auto px-6 pt-4">
            <div
              role="status"
              className={`flex items-center gap-3 p-3 rounded-2xl border ${
                activeScope === 'PERSONAL'
                ? 'bg-slate-100 border-slate-200 dark:bg-slate-800/50 dark:border-slate-800 text-slate-600 dark:text-slate-300'
                : 'bg-primary-50 border-primary-100 dark:bg-primary-900/20 dark:border-primary-800 text-primary-600 dark:text-primary-300'
              }`}>
              {activeScope === 'PERSONAL' ? (
                <User className="w-4 h-4 text-slate-500 dark:text-slate-300" />
              ) : (
                <Users className="w-4 h-4 text-primary-600 dark:text-primary-300" />
              )}
              <span className="text-2xs font-black uppercase tracking-wider">
                {activeScope === 'PERSONAL'
                  ? t('dashboard.scope_personal', 'Private Partition')
                  : activeScope === 'SHARED'
                  ? t('dashboard.scope_shared', 'Household Hub')
                  : t('dashboard.scope_both', 'Hybrid Stream')} {t('dashboard.scope_active', 'Active')}
              </span>
            </div>
          </div>

          <div className="max-w-[1600px] mx-auto px-6">
            <DashboardGrid
              layouts={layouts.filter(w => {
                if (roleView === 'admin') return true;
                if (roleView === 'child') return !ADMIN_ONLY_WIDGETS.includes(w.i as WidgetId);
                return !MEMBER_HIDDEN_WIDGETS.includes(w.i as WidgetId);
              })}
              isEditMode={isEditMode}
              onLayoutChange={onLayoutChange}
              onRemoveWidget={removeWidget}
              widgetRegistry={widgetRegistry}
              currentUserId={state.currentUser!.id}
              currentHouseholdId={state.currentHouseholdId}
              activeScope={activeScope}
            />
          </div>
        </main>

        <WidgetSidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onAddWidget={(id, defaultW, defaultH) => {
            addWidget(id, defaultW, defaultH);
            setIsSidebarOpen(false);
          }}
          activeWidgetIds={layouts.map(l => l.i)}
          currentHouseholdId={state.currentHouseholdId}
          conditionalFlags={conditionalFlags}
        />
      </div>

      <GettingStartedWidget />

      {hasUnsavedChanges && !isEditMode && (
        <div role="status" aria-live="polite" className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-bottom-8 duration-500">
          <button
            onClick={() => {
              saveLayout();
              dispatch({ type: 'UPDATE_LAYOUT', payload: layouts });
            }}
            className="bg-primary-600 text-white px-8 py-4 rounded-full font-black text-2xs uppercase tracking-widest shadow-lg shadow-primary-500/30 hover:bg-primary-700 hover:scale-105 transition-all active:scale-95 flex items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-600 focus-visible:ring-white"
          >
            <Save className="w-4 h-4 mr-2" />
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            Commit Layout Changes
          </button>
        </div>
      )}
    </div>
  );
};